// Pouso de foco na ENTRADA/VOLTA de tela — a fiação do `decideEntryFocus`.
//
// Fecha em Home/Movies/Series a MESMA classe de bug que a Busca teve no board
// 2.21.0: `[data-focused]` null, D-pad morto, só o Back respondendo. A causa
// não é foco faltando, é foco MENTIDO — `setFocus` numa key não registrada
// blura o foco atual, grava a key e não foca nada, e aí
// `getCurrentFocusKey() === alvo` (a checagem que estava nos 7 sites) vira
// TAUTOLOGIA: dá true com a tela órfã, o retry encerra na 1ª volta e o
// fallback nunca roda. Ver o banner de `lib/entry-focus.ts`.
//
// Este hook substitui o retry-loop copiado 7 vezes por uma implementação só,
// com a decisão isolada numa função pura (testada em `lib/entry-focus.test.ts`).

import { doesFocusableExist, getCurrentFocusKey, setFocus } from '@app-iptv/ui'
import { useEffect } from 'react'

import { decideEntryFocus, screenHasFocus } from '../lib/entry-focus'

/** Mesmos números do retry que já existia nas telas (20 × 60ms ≈ 1,2s). */
const ENTRY_FOCUS_TRIES = 20
const ENTRY_FOCUS_RETRY_MS = 60

/**
 * Versão IMPERATIVA: dispara um pouso e devolve o cancelador.
 *
 * Existe pros casos em que o alvo é um EVENTO, não um estado de render —
 * entrar/sair de um drill de categoria, por exemplo. Ali o alvo pode repetir
 * (entrar de novo na MESMA categoria), então guardar num state não
 * re-dispararia; e um state a mais custaria um render inteiro na transição,
 * justo onde o vlow Tizen 2017 não tem folga. Chamada de dentro do effect que
 * já existe, devolvendo o cleanup dele.
 */
export function startEntryFocus({
  targetKey,
  safeKey,
  tries = ENTRY_FOCUS_TRIES,
  retryMs = ENTRY_FOCUS_RETRY_MS,
}: EntryFocusOptions & { targetKey: string }): () => void {
  // Ponto de partida: serve pra distinguir "o usuário moveu o foco" de
  // "o foco ainda está onde estava quando entramos".
  const initialKey = getCurrentFocusKey() || ''
  let triesLeft = tries
  let cancelled = false
  let timer: ReturnType<typeof setTimeout> | null = null
  // Tentativas de pouso no alvo que NÃO se confirmaram. Um pouso confirmado
  // encerra o loop (regra 2), então todo tick seguinte a um `focus-target`
  // prova que aquela tentativa falhou — daí bastar incrementar aqui.
  let targetAttempts = 0
  // Ticks gastos esperando um alvo não-registrado com a tela já focada — o que
  // bounda a cauda ociosa do retry (ver `MAX_WAIT_FOR_TARGET`).
  let waitAttempts = 0

  const tick = (): void => {
    if (cancelled) return

    const targetExists = doesFocusableExist(targetKey)
    // Com o `owner`: um `[data-focused]` cujo dono foi DESREGISTRADO (grid
    // virtualizado desmontando o card focado) não conta como foco. Ali as setas
    // já estão mortas, e sem o cruzamento a rede não entrava.
    //
    // JANELA ESTREITA CONHECIDA: o cruzamento é por key, e a key de um elemento
    // focado pode MUDAR no meio do loop (re-registro com outro focusKey). Nesse
    // instante o dono novo ainda não consta e a rede pode puxar pro `safeKey`,
    // roubando um foco que era legítimo. A exposição é pequena — o loop encerra
    // assim que o alvo pousa, e re-registro durante a ENTRADA de tela é raro —,
    // e o custo de errar (foco no TopNav) é muito menor que o de não entrar
    // (D-pad morto). Documentado em vez de tratado de propósito.
    const hasFocus = screenHasFocus(document, {
      currentKey: getCurrentFocusKey(),
      isRegistered: doesFocusableExist,
    })

    const action = decideEntryFocus({
      targetKey,
      safeKey,
      initialKey,
      currentKey: getCurrentFocusKey() || '',
      targetExists,
      safeExists: doesFocusableExist(safeKey),
      hasFocus,
      triesLeft,
      targetAttempts,
      waitAttempts,
    })

    switch (action.kind) {
      case 'settled':
        return
      case 'focus-target':
        targetAttempts++
        setFocus(targetKey)
        // O pouso é confirmado pelo DOM no tick seguinte: se por algum motivo
        // não pousou, o orçamento ainda cobre e a rede de segurança entra.
        break
      case 'focus-safe':
        setFocus(safeKey)
        break
      case 'retry':
        // Só conta como ESPERA o tick que não fez nada tendo a tela já focada:
        // é exatamente a volta ociosa que queremos boundar.
        if (hasFocus && !targetExists) waitAttempts++
        break
    }

    if (triesLeft-- <= 0) return
    timer = setTimeout(tick, retryMs)
  }

  // rAF pra dar ao React a chance de montar/registrar antes da 1ª tentativa.
  const raf = requestAnimationFrame(tick)
  return () => {
    cancelled = true
    cancelAnimationFrame(raf)
    if (timer) clearTimeout(timer)
  }
}

export interface EntryFocusOptions {
  /**
   * Alvo desejado. `null` desliga o hook (ex.: não há âncora curada a
   * consumir, ou a tela não está entrando em drill nenhum).
   */
  targetKey: string | null
  /**
   * Alvo SEMPRE montado da tela — na prática o item de TopNav (`nav-home`,
   * `nav-movies`, `nav-series`). É a rede de segurança que garante a
   * invariante "a tela nunca fica sem foco".
   */
  safeKey: string
  /** Tentativas do orçamento. Default 20 (~1,2s com o intervalo padrão). */
  tries?: number
  /** Intervalo entre tentativas, em ms. Default 60. */
  retryMs?: number
}

/**
 * Garante que a tela SEMPRE termine com um foco pousado ao entrar/voltar:
 * tenta o `targetKey` enquanto ele não montar, mas nunca deixa o documento
 * órfão no meio do caminho — se não há `[data-focused]` em nenhum tick, a
 * rede de segurança pousa no `safeKey` na hora.
 *
 * O effect roda UMA vez por mudança de `targetKey` (a lista de deps é
 * intencionalmente enxuta: `safeKey`/`tries`/`retryMs` são constantes por
 * tela). Cleanup cancela rAF e timer — nada sobrevive à saída da tela.
 */
export function useEntryFocus({
  targetKey,
  safeKey,
  tries = ENTRY_FOCUS_TRIES,
  retryMs = ENTRY_FOCUS_RETRY_MS,
}: EntryFocusOptions): void {
  useEffect(() => {
    if (!targetKey) return undefined
    return startEntryFocus({ targetKey, safeKey, tries, retryMs })
  }, [targetKey, safeKey, tries, retryMs])
}
